import { useEffect, useSyncExternalStore } from "react";
import type { ReactNode } from "react";
import { Icon } from "./icon.js";
import type { IconName } from "./icon.js";
import type { Translate } from "./types.js";

export type ThemePreference = "system" | "light" | "dark";
/** localStorage key; the inline script in each app's index.html reads the same key before paint. */
export const THEME_KEY = "uz.theme";
export const THEME_ORDER: readonly ThemePreference[] = ["system", "light", "dark"];

export function isThemePreference(v: unknown): v is ThemePreference {
  return v === "system" || v === "light" || v === "dark";
}

function stored(): ThemePreference {
  try {
    const v = globalThis.localStorage.getItem(THEME_KEY);
    if (v === "light" || v === "dark") return v;
  } catch {
    // Storage may be blocked; follow the OS.
  }
  return "system";
}

/** Applies a preference to <html>: `system` removes the override so the OS decides. */
export function applyTheme(pref: ThemePreference): void {
  const root = document.documentElement;
  if (pref === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", pref);
  try {
    if (pref === "system") globalThis.localStorage.removeItem(THEME_KEY);
    else globalThis.localStorage.setItem(THEME_KEY, pref);
  } catch {
    // The choice then lasts for this visit only.
  }
}

// ---------------------------------------------------------------- the store

let current: ThemePreference = stored();
const listeners = new Set<() => void>();
const saveHooks = new Set<(pref: ThemePreference) => void>();

export function getThemePreference(): ThemePreference {
  return current;
}

export function subscribeTheme(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * The user's own choice (toggle, settings): applied, cached in localStorage and handed to the
 * save hooks (the web app registers one that PATCHes `/api/v1/settings`).
 */
export function setThemePreference(pref: ThemePreference): void {
  if (pref !== current) {
    current = pref;
    for (const l of listeners) l();
  }
  applyTheme(pref);
  for (const h of saveHooks) h(pref);
}

/** A value that came from the server: applied and cached, but not echoed back. */
export function adoptThemePreference(pref: ThemePreference): void {
  if (pref === current) return;
  current = pref;
  applyTheme(pref);
  for (const l of listeners) l();
}

/** Registers a callback for user-made changes; returns its unregister function. */
export function onThemeSave(fn: (pref: ThemePreference) => void): () => void {
  saveHooks.add(fn);
  return () => saveHooks.delete(fn);
}

export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribeTheme, getThemePreference, () => "system");
}

/** Call once before the first render so there is no flash of the wrong theme. */
export function initTheme(): void {
  current = stored();
  if (current !== "system") document.documentElement.setAttribute("data-theme", current);
}

const ICON: Record<ThemePreference, IconName> = {
  system: "monitor",
  light: "sun",
  dark: "moon",
};

/** Cycles system, light, dark. The accessible name states the current choice. */
export function ThemeToggle(props: { t: Translate }): ReactNode {
  const { t } = props;
  const pref = useThemePreference();
  useEffect(() => {
    applyTheme(pref);
  }, [pref]);
  const label = `${t("ui.theme.label")}: ${t(`ui.theme.${pref}`)}`;
  return (
    <button
      type="button"
      className="btn btn-quiet btn-icon theme-toggle"
      aria-label={label}
      title={label}
      data-testid="theme-toggle"
      data-theme-pref={pref}
      onClick={() =>
        setThemePreference(THEME_ORDER[(THEME_ORDER.indexOf(pref) + 1) % THEME_ORDER.length]!)
      }
    >
      <Icon name={ICON[pref]} />
    </button>
  );
}

/** Three explicit buttons (auto, light, dark) for the settings page. */
export function ThemeSelect(props: { t: Translate }): ReactNode {
  const { t } = props;
  const pref = useThemePreference();
  return (
    <div
      className="row theme-select"
      role="group"
      aria-label={t("ui.theme.label")}
      data-testid="theme-select"
    >
      {THEME_ORDER.map((p) => (
        <button
          key={p}
          type="button"
          className={`btn ${p === pref ? "btn-primary" : "btn-quiet"}`}
          aria-pressed={p === pref}
          data-testid={`theme-${p}`}
          onClick={() => setThemePreference(p)}
        >
          <Icon name={ICON[p]} /> {t(`ui.theme.${p}`)}
        </button>
      ))}
    </div>
  );
}
